import DIMENSIONS from '@app/config/dimensions';
import { MenuItemOfBag } from '@app/contexts/Bag';
import { APICliqueiAchei } from '@app/services/cliqueiachei-api';
import { formsAPI } from '@app/services/forms-api';
import { AddressStorage, FavoritedMenuItemsStorage, UserStorage } from '@app/services/storage';
import { Address, City, CliqueiAcheiData, DeliveryTypesAvailable, Menu, Restaurant, User } from '@app/types';
import { MenuItemWithDetailsOfBag } from '@app/types/menu-item';
import { getBRCurrencyFormat } from '@app/utils/formmater';
import { PaymentAvailable } from '@app/utils/payments';
import { useEffect, useMemo, useState } from 'react';

export function useDeliveryTypesAvailable(cliqueiAcheiData: CliqueiAcheiData, restaurant: Restaurant) {
  const value = useMemo(() => {
    const types = [];
    if (cliqueiAcheiData?.Entrega) {
      types.push(DeliveryTypesAvailable.HomeDelivery);
    }
    if (restaurant?.aceitaRetiradaNoLocal) {
      types.push(DeliveryTypesAvailable.LocalPickup);
    }
    return types;
  }, [cliqueiAcheiData, restaurant]);

  return value;
}

const PAYMENTS_ENABLED = [PaymentAvailable.CreditCard, PaymentAvailable.DebitCard, PaymentAvailable.Money];

export function usePaymentsAvailable(cliqueiAcheiData: CliqueiAcheiData, restaurant: Restaurant) {
  const value = useMemo(() => {
    const payments = getEnabledPayments();
    addPixIfConfigured();
    return payments;

    function getEnabledPayments() {
      const allPayments = cliqueiAcheiData?.FormasPagamento;
      return allPayments?.filter((payment) => PAYMENTS_ENABLED.includes(payment as PaymentAvailable)) || [];
    }

    function addPixIfConfigured() {
      const configurations = restaurant?.Configuracoes;
      if (configurations?.liberarPagamentoPorPix && configurations?.chavePIX) {
        payments.push(PaymentAvailable.PIX);
      }
    }
  }, [cliqueiAcheiData, restaurant]);

  return value;
}

export function useLoadAddress(
  restaurant: Restaurant,
  cliqueiAcheiData: CliqueiAcheiData,
  isLoggedInClientArea: boolean,
  onAddress: (address: Address | null) => void
) {
  useEffect(() => {
    if (isLoggedInClientArea) {
      onAddress(null);
      return;
    }

    if (restaurant.Configuracoes?.entregaEmOutrasCidades) {
      const addresses = AddressStorage.get();
      onAddress(addresses[0] ?? null);
    } else {
      onAddress(AddressStorage.getByCityUrl(cliqueiAcheiData.Url_Cidade));
    }
  }, [isLoggedInClientArea]);
}

export function useLoadUser(isLoggedInClientArea: boolean, onUser: (user: User | null) => void) {
  useEffect(() => {
    if (isLoggedInClientArea) {
      onUser(null);
      return;
    }

    onUser(UserStorage.get());
  }, [isLoggedInClientArea]);
}

export async function checkIsRestaurantIsOpen(cliqueiAcheiId: number) {
  const { data } = await APICliqueiAchei.fetchClientDataById(cliqueiAcheiId);
  return !!data.Aberto;
}

export const BagAlertsData = {
  unableDefineOpenStatus(restaurantName: string) {
    return {
      title: ``,
      message: `Não conseguimos verificar se ${restaurantName} está aberto ou fechado. Verifique a sua conexão com a internet.`,
    };
  },
  menuViewOnly(restaurantName: string) {
    return {
      title: 'Peça para o garçom',
      message: `A ${restaurantName} configurou este cardápio somente para visualização. Após escolher, chame o garçom e faça o seu pedido!`,
    };
  },
  restaurantClosed() {
    return { title: 'Estamos fechados', message: 'No momento estamos fora do nosso horário de atendimento.' };
  },
  missingPayments() {
    return {
      title: 'Problema de Configuração',
      message: 'O estabelecimento ainda não cadastrou suas Formas de Pagamento.',
    };
  },
  missingDeliveryOption() {
    return { title: 'Problema de Configuração', message: 'O estabelecimento ainda não informou se faz entregas.' };
  },
  missingWhatsapp() {
    return {
      title: 'Problema de Configuração',
      message: 'Este estabelecimento não cadastrou um WhatsApp para receber os pedidos.',
    };
  },
  missingSelectAddress() {
    return {
      title: 'Falta pouco...',
      message: 'Para finalizar o pedido é necessário cadastrar um endereço de entrega.',
    };
  },
  minimumOrderValueNotSatisfied(minimumValue: number) {
    const minValue = getBRCurrencyFormat(minimumValue);
    return { title: 'Pedido Mínimo', message: `Para fazer o pedido sua sacola deve ter o valor mínimo de ${minValue}` };
  },
  missingSelectPayment(payments: string[]) {
    return { title: 'Formas de Pagamento', message: 'Para continuar selecione uma forma de pagamento', payments };
  },
  missingSelectDeliveryType(deliveryTypes: DeliveryTypesAvailable[]) {
    const radios = deliveryTypes.map((deliveryType) => ({
      value: deliveryType,
      label: deliveryType,
    }));
    return { title: 'Tipo de Entrega', message: 'Para continuar selecione uma forma de entrega', radios };
  },
  dontNeedChange(totalOrder: number) {
    const totalFormatted = getBRCurrencyFormat(totalOrder);
    return {
      title: 'Você não precisa de troco',
      message: `Sua conta deu ${totalFormatted} e você informou que precisa de troco para ${totalFormatted}, por isso entendemos que você tem o valor trocado para pagar. Informaremos o estabelecimento que você não precisa de troco.`,
      buttons: [{ text: 'Editar' }, { text: 'Continuar' }],
    };
  },
  invalidChangeValue(totalOrder: number, changeValue: number) {
    const totalFormatted = getBRCurrencyFormat(totalOrder);
    return {
      title: 'Valor inválido no campo de troco',
      message: `Você deve informar um valor maior que o preço total da sua compra, no caso um valor maior que ${totalFormatted}`,
      minValue: totalOrder,
      initialValue: changeValue,
      positiveButtonText: 'Confirmar',
      negativeButtonText: 'Cancelar',
    };
  },
  changeNotInformed() {
    return {
      title: 'Atenção!',
      message: 'Você não informou se é preciso levar troco',
      buttons: [{ text: 'Informar troco' }, { text: 'Não precisa de troco' }],
    };
  },
  orderRegisteredSuccessfully() {
    return {
      title: 'Aviso',
      message: 'Pedido registrado com sucesso!',
    };
  },
  cityNotFound() {
    return {
      title: 'Ocorreu um erro',
      message: 'Não conseguimos localizar a cidade do estabelecimento!',
    };
  },
};

const MAX_TOP_POSITION = DIMENSIONS.APP_BAR.HEIGHT + DIMENSIONS.APP_BAR.BOTTOM_SPACING;

export function useTopPosition(bagContainerElement: HTMLElement | null) {
  const [top, setTop] = useState(MAX_TOP_POSITION);

  useEffect(() => {
    let lastScrollYPosition = window.scrollY;

    function handleScroll() {
      const scrollYPosition = window.scrollY;

      const containerRect = bagContainerElement?.getBoundingClientRect();
      const bagHeight = containerRect?.height ?? 0;

      if (bagHeight < window.innerHeight - DIMENSIONS.APP_BAR.HEIGHT) {
        setTop(MAX_TOP_POSITION);
        return;
      }

      const minTopPosition = window.innerHeight - (bagHeight + DIMENSIONS.APP_BAR.BOTTOM_SPACING);

      const isScrollingDown = scrollYPosition > lastScrollYPosition;
      if (isScrollingDown) {
        const scrollDiff = scrollYPosition - lastScrollYPosition;
        setTop((prevTop) => {
          if (prevTop - scrollDiff <= minTopPosition || prevTop <= minTopPosition) return minTopPosition;
          return prevTop - scrollDiff;
        });
      } else {
        const scrollDiff = lastScrollYPosition - scrollYPosition;
        setTop((prevTop) => {
          if (prevTop + scrollDiff >= MAX_TOP_POSITION || prevTop >= MAX_TOP_POSITION) return MAX_TOP_POSITION;
          return prevTop + scrollDiff;
        });
      }

      lastScrollYPosition = scrollYPosition < 0 ? 0 : scrollYPosition;
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [bagContainerElement]);

  return top;
}

enum InsightsTypes {
  WhatsappDelivery = 'Pedidos Whatsapp',
}

export type InsightPayload = {
  city: City;
  date: number;
  restaurant: {
    id: string;
    name: string;
  };
  totalOrder: number;
  payment: string;
  origin: string;
};

export async function sendDeliveryInsight(data: InsightPayload) {
  await formsAPI.post(`/delivery-insights-${data.city.Url_Cidade}`, {
    type: InsightsTypes.WhatsappDelivery,
    date: data.date,
    city: data.city,
    restaurant: {
      id: data.restaurant.id,
      name: data.restaurant.name,
    },
    totalOrder: data.totalOrder,
    payment: data.payment,
    origin: data.origin,
  });
}

export function selectMenuItemsNotFavorited(menuItems: MenuItemWithDetailsOfBag[]) {
  const menuItemsIdsFavorited = FavoritedMenuItemsStorage.getAll().map((info) => info.id);
  return menuItems.filter((menuItem) => !menuItemsIdsFavorited.includes(menuItem.id));
}

const FAVORITE_ALERT_MESSAGES = {
  MESSAGE: 'Você gostaria de salvar este produto como Favorito para a sua próxima compra?',
  MESSAGE_PLURAL: 'Você gostaria de salvar estes produtos como Favoritos para a sua próxima compra?',
};

export function favoriteMessage(amountNotFavorited: number) {
  return amountNotFavorited > 1 ? FAVORITE_ALERT_MESSAGES.MESSAGE_PLURAL : FAVORITE_ALERT_MESSAGES.MESSAGE;
}

export function sortItemsByMenusPosition(items: MenuItemOfBag[], menus: Menu[]) {
  const onlyMenusFromItems = selectOnlyMenusFromItems();
  const menusPositions = mapMenusByPosition();
  const itemsPositionsInItsMenu = mapItemsByItsPosition();

  const result = items.sort((itemA, itemB) => {
    const itemAMenuId = itemA.categoriaId;
    const itemBMenuId = itemB.categoriaId;

    if (itemAMenuId === itemBMenuId) {
      const itemsPositions = itemsPositionsInItsMenu[itemAMenuId];
      return itemsPositions[itemA.id] < itemsPositions[itemB.id] ? -1 : 1;
    }

    return menusPositions[itemAMenuId] < menusPositions[itemBMenuId] ? -1 : 1;
  });

  return result;

  function mapMenusByPosition() {
    return onlyMenusFromItems.reduce<{ [menuId: string]: number }>((previous, menu) => {
      return Object.assign(previous, { [menu.id]: menu.ordem });
    }, {});
  }

  function mapItemsByItsPosition() {
    return onlyMenusFromItems.reduce<{ [menuId: string]: { [itemId: string]: number } }>((previous, menu) => {
      previous[menu.id] = menu.itens.reduce<{ [itemId: string]: number }>((current, item, index) => {
        return Object.assign(current, { [item.id]: index });
      }, {});
      return previous;
    }, {});
  }

  function selectOnlyMenusFromItems() {
    const itemsMenusIds = items.map((item) => item.categoriaId);
    return menus.filter((menu) => itemsMenusIds.includes(menu.id));
  }
}
